import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { freshness, nextSeen, readSeen, writeSeen, type Seen } from './queueFreshness'

type Row = { ID: number; detections: number }

/**
 * Semnele New / Updated ale cozii. Cand pleci de pe fila sau de pe pagina, ce
 * vezi devine vazut pentru data viitoare; semnele de pe ecran raman pana te
 * intorci (sau pana la "Mark all seen"). Titlul filei poarta numarul lor.
 */
export function useQueueFreshness(rows: Row[], loaded: boolean) {
  const [since, setSince] = useState<Seen | null>(() => readSeen())
  const rowsRef = useRef(rows)
  rowsRef.current = rows

  // prima privire: nimic marcat, dar coada de acum devine reperul
  useEffect(() => {
    if (since !== null || !loaded) return
    const baseline = nextSeen(rows, null)
    writeSeen(baseline)
    setSince(baseline)
  }, [since, loaded, rows])

  useEffect(() => {
    const remember = () => writeSeen(nextSeen(rowsRef.current, readSeen()))
    const onVisibility = () => {
      if (document.visibilityState === 'hidden') remember()
      else setSince(readSeen())
    }
    document.addEventListener('visibilitychange', onVisibility)
    return () => {
      document.removeEventListener('visibilitychange', onVisibility)
      remember()
    }
  }, [])

  const fresh = useMemo(() => freshness(rows, since), [rows, since])

  useEffect(() => {
    if (fresh.size === 0) return
    const title = document.title
    document.title = `(${fresh.size}) ${title}`
    return () => {
      document.title = title
    }
  }, [fresh.size])

  const markAllSeen = useCallback(() => {
    const seen = nextSeen(rowsRef.current, readSeen())
    writeSeen(seen)
    setSince(seen)
  }, [])

  return { fresh, markAllSeen }
}
